import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown';
import { Checkbox, FormControlLabel, FormGroup, IconButton } from '@mui/material';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { IAddressViewModel, IDistrictViewModel } from 'viewModels';
import { Controller, useForm } from 'react-hook-form';
import { IRefRegisterServiceData, IRegisterServiceData } from 'entities';
import { Lock, Refresh, Search } from '@mui/icons-material';
import { toPattern } from 'vanilla-masker';
import { useApp, useRegisterForm } from '../../context';
import { getCodeByName } from '../../services/api/ibge';
import { TypeUser } from '../../types/enums';
import { unMask } from '../../utils/removeMask';
import { slugify } from '../../utils/slugify';
import { Accordion } from '../Accordion';
import { Container, Content } from './styles';
import { useDistricts } from '../../context/districts';
import { MASK_CEP, MASK_NUMBER_ADDRESS, UF_KEYS } from '../../constants';
import { changeSpaceToUnderline } from '../../utils/changeSpaceToUnderline';
import { UpdateDistrictsOfOperationModal } from '../Modals/ModalUpdateDistrictsOfOperation';
import { Input } from '../Inputs/Input';
import InformationBaseZipCodeController from '../../controller/InformationBaseZipCodeController';
import { useAlert } from '../../hooks/useAlert';
import { handleGetAddressError } from '../../utils/handleGetAddressError';

export const RegisterService = forwardRef<IRefRegisterServiceData>((_, ref) => {
  const refComponent = useRef<HTMLDivElement>(null);
  const [defaultValues] = useState({
    address: {} as IAddressViewModel,
    isServiceProvider: false,
  });
  const {
    activeForm,
    setActiveForm,
    formSubmited,
    address,
    isServiceProvider,
    setIsServiceProviderCurrentSelection,
    userType,
  } = useRegisterForm();

  const { setLoading } = useApp();
  const alert = useAlert();
  const location = useLocation();
  const { districtsSelectedsByUser } = useDistricts();
  const [isZipCodeWithOutStreet, setIsZipCodeWithOutStreet] = useState(false);
  const [isOpenEditDistricts, setIsOpenEditDistricts] = useState(false);
  const [displaySummary, setDisplaySummary] = useState(true);
  const [districtsSelected, setDistrictsSelected] = useState<IDistrictViewModel[]>([]);
  const color =
    location.pathname === '/naturalPersonAccount' || location.pathname === '/legalPersonAccount'
      ? 'background'
      : 'background_white';
  const {
    control,
    setError,
    setValue,
    clearErrors,
    watch,
    resetField,
    formState: { errors },
  } = useForm({
    reValidateMode: 'onChange',
    defaultValues,
  });

  const data = watch();
  useEffect(() => {
    if (activeForm === 'registerService') {
      setDisplaySummary(false);
    } else {
      setDisplaySummary(true);
    }
  }, [activeForm]);

  const checkErrors = useCallback(() => {
    let hasError = false;
    if (data.isServiceProvider && userType === TypeUser.USER) {
      const postalCode = unMask(data.address?.postalCode ?? '');
      if (!postalCode) {
        setError('address.postalCode', {
          type: 'required',
          message: 'CEP é obrigatório',
        });
        hasError = true;
      } else if (postalCode.length === 9) {
        setError('address.postalCode', {
          type: 'required',
          message: 'CEP inválido',
        });
        hasError = true;
      } else {
        clearErrors('address.postalCode');
      }
      if (!data.address.city) {
        setError('address.city', {
          type: 'required',
          message: 'Cidade é obrigatório',
        });
        hasError = true;
      } else {
        clearErrors('address.city');
      }
      if (!data.address.uf) {
        setError('address.uf', {
          type: 'required',
          message: 'Estado é obrigatório',
        });
        hasError = true;
      } else {
        clearErrors('address.uf');
      }
      if (!data.address.street) {
        setError('address.street', {
          type: 'required',
          message: 'Rua é obrigatório',
        });
        hasError = true;
      } else {
        clearErrors('address.street');
      }
      if (!data.address.number) {
        setError('address.number', {
          type: 'required',
          message: 'Nº é obrigatório',
        });
        hasError = true;
      } else {
        clearErrors('address.number');
      }
    }
    return hasError;
  }, [
    clearErrors,
    data.address.city,
    data.address.number,
    data.address.postalCode,
    data.address.street,
    data.address.uf,
    data.isServiceProvider,
    setError,
    userType,
  ]);

  const handleHiddenSummary = () => {
    setDisplaySummary(!displaySummary);
    setActiveForm('registerService');
  };
  const handleSearchAddress = async () => {
    if (!data.address.postalCode) return;
    const postalCode = unMask(data.address.postalCode);
    if ((postalCode.length === 7 && postalCode[0] !== '0') || postalCode.length === 8) {
      const newPostalCode = postalCode.length === 7 ? `0${postalCode}` : postalCode;
      if (newPostalCode.length === 8) {
        setLoading(true);
        resetField('address.city');
        resetField('address.uf');
        resetField('address.street');
        resetField('address.number');
        resetField('address.complement');
        await InformationBaseZipCodeController.getZipCodeWithGeoLocation(newPostalCode)
          .then(async (responseAddress) => {
            if (!responseAddress?.city || !responseAddress?.uf) {
              alert.show({ message: 'Não foi possível localizar o CEP informado', type: 'error' });
            }
            if (responseAddress.street) {
              setIsZipCodeWithOutStreet(false);
            } else {
              setIsZipCodeWithOutStreet(true);
            }
            if (responseAddress.city && responseAddress.uf) {
              const addressWithDataIbge = await getCodeByName(
                slugify(responseAddress.city),
                slugify(responseAddress.uf),
              );
              const addressData: IAddressViewModel = {
                city: responseAddress.city,
                cityCode: addressWithDataIbge?.id ? Number(addressWithDataIbge.id) : 0,
                uf: responseAddress.uf,
                ufCode: addressWithDataIbge?.ufCode ?? 0,
                street: responseAddress.street,
                number: Number(data.address.number),
                postalCode: responseAddress.zipCode,
                latitude: responseAddress.latitude ?? 0,
                longitude: responseAddress.longitude ?? 0,
                complement: data.address.complement,
              };
              setValue('address.city', addressData.city);
              setValue('address.uf', addressData.uf);
              setValue('address.street', addressData.street);
              setValue('address.postalCode', toPattern(addressData.postalCode ?? '', MASK_CEP));
              setValue('address.number', addressData.number);
              setValue('address.latitude', addressData.latitude);
              setValue('address.longitude', addressData.longitude);
              setValue('address.complement', addressData.complement);
              setValue('address.cityCode', addressData.cityCode);
              setValue('address.ufCode', addressData.ufCode);
            }
          })
          .catch(() => {
            alert.show({ message: 'CEP inválido', type: 'error' });
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        alert.show({ message: 'CEP inválido', type: 'error' });
      }
    }
  };

  // const hasChangedAddress = () => {
  //   let hasChanged = false;
  //   if (data.address.postalCode && data.address.postalCode !== addressField.postalCode) {
  //     hasChanged = true;
  //   }
  //   if (data.address.city && data.address.city !== addressField.city) {
  //     hasChanged = true;
  //   }
  //   if (data.address.uf && data.address.uf !== addressField.uf) {
  //     hasChanged = true;
  //   }
  //   if (data.address.street && data.address.street !== addressField.street) {
  //     hasChanged = true;
  //   }
  //   if (data.address.number && data.address.number !== addressField.number) {
  //     hasChanged = true;
  //   }
  //   if (data.address.complement && data.address.complement !== addressField.complement) {
  //     hasChanged = true;
  //   }
  //   return hasChanged;
  // };

  useEffect(() => {
    if (userType === TypeUser.USER) {
      setValue('address.postalCode', toPattern(address?.postalCode ?? '', MASK_CEP));
      setValue('address.city', address?.city);
      setValue('address.uf', address?.uf);
      setValue('address.number', address?.number);
      setValue('address.street', address?.street);
      setValue('address.complement', address?.complement);
      setValue('address.cityCode', address?.cityCode);
      setValue('address.ufCode', address?.ufCode);
      setValue('address.latitude', address?.latitude);
      setValue('address.longitude', address?.longitude);
    }

    setValue('isServiceProvider', isServiceProvider);
    setDistrictsSelected(districtsSelectedsByUser);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    address?.city,
    address?.complement,
    address?.number,
    address?.postalCode,
    address?.street,
    address?.uf,
    districtsSelectedsByUser,
    isServiceProvider,
    userType,
  ]);

  useEffect(() => {
    if (data.address.postalCode) {
      clearErrors('address.postalCode');
    }
    if (data.address.city) {
      clearErrors('address.city');
    }
    if (data.address.uf) {
      clearErrors('address.uf');
    }
    if (data.address.street) {
      clearErrors('address.street');
    }
    if (data.address.number) {
      clearErrors('address.number');
    }
  }, [
    clearErrors,
    data.address.city,
    data.address.number,
    data.address.postalCode,
    data.address.street,
    data.address.uf,
  ]);

  const handleUpdateServiceData = () => {
    const hasError = checkErrors() || (data.isServiceProvider && !districtsSelected.length);
    const newAddress: IAddressViewModel = {
      city: data.address.city?.trim(),
      uf: data.address.uf?.trim(),
      postalCode: data.address.postalCode?.trim(),
      street: data.address.street?.trim(),
      number: data.address.number ? Number(data.address.number) : 0,
      complement: data.address.complement,
      latitude: data.address.latitude,
      longitude: data.address.longitude,
      cityCode: data.address.cityCode,
      ufCode: data.address.ufCode,
    };
    const newData: IRegisterServiceData = {
      address: data.isServiceProvider ? newAddress : undefined,
      isProviderService: data.isServiceProvider,
      hasError,
    };
    return newData;
  };
  const handleFocusOnError = () => {
    refComponent.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useImperativeHandle(ref, () => ({
    handleUpdateServiceData,
    handleFocusOnError,
  }));

  const handleResetAddressFields = () => {
    setValue('address.city', '');
    setValue('address.uf', '');
    setValue('address.street', '');
    setValue('address.number', 0);
    setValue('address.postalCode', '');
    setValue('address.complement', '');
    setIsZipCodeWithOutStreet(false);
  };
  return (
    <Container color={color} ref={refComponent}>
      {!displaySummary && (
        <Content>
          <div>
            <div className="headerContent">
              <h1>Você deseja prestar algum tipo serviço?</h1>
              <IconButton onClick={handleHiddenSummary}>
                <ExpandCircleDownIcon
                  color={
                    Object.keys(data.address).length > 0 && userType === TypeUser.USER
                      ? 'success'
                      : 'error'
                  }
                  fontSize="large"
                />
              </IconButton>
            </div>
            <div className="containerCheckbox">
              <FormGroup row>
                <Controller
                  control={control}
                  name="isServiceProvider"
                  render={({ field: { value, onChange, ...rest } }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          id="check-yes"
                          checked={value}
                          onChange={() => {
                            onChange(true);
                            setIsServiceProviderCurrentSelection(true);
                          }}
                          {...rest}
                        />
                      }
                      label=" Sim"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="isServiceProvider"
                  render={({ field: { value, onChange, ...rest } }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          id="check-no"
                          className="checkbox"
                          checked={!value}
                          onChange={() => {
                            onChange(false);
                            setIsServiceProviderCurrentSelection(false);
                          }}
                          {...rest}
                        />
                      }
                      label=" Não"
                    />
                  )}
                />
              </FormGroup>
            </div>
          </div>
          <div className="containerAccordions">
            {data.isServiceProvider && userType === TypeUser.USER && (
              <div>
                <Accordion title="Seu endereço" isOpen onOpen={() => {}} type="default">
                  <div className="contentAddress">
                    <div className="header">
                      <Input
                        name="address.postalCode"
                        placeholder="CEP"
                        value={address.postalCode?.replace(/\D/g, '') ?? ''}
                        endAdornment={
                          isZipCodeWithOutStreet ? (
                            <IconButton
                              onClick={() => {
                                handleResetAddressFields();
                              }}>
                              <Refresh />
                            </IconButton>
                          ) : (
                            <IconButton
                              onClick={() => {
                                handleSearchAddress();
                              }}>
                              <Search />
                            </IconButton>
                          )
                        }
                        control={control}
                        variant="standard"
                        lengthInput={9}
                        mask={MASK_CEP}
                        preventNotNumbers
                        disabled={isZipCodeWithOutStreet}
                        error={formSubmited ? !!errors.address?.postalCode : false}
                        errorText={formSubmited ? errors.address?.postalCode?.message : ''}
                      />
                      <Input
                        name="address.city"
                        placeholder="Cidade"
                        value={address.city ?? ''}
                        control={control}
                        variant="standard"
                        lengthInput={30}
                        error={formSubmited ? !!errors.address?.city : false}
                        errorText={formSubmited ? errors.address?.city?.message : ''}
                        endAdornment={
                          <IconButton disabled>
                            <Lock />
                          </IconButton>
                        }
                        disabled
                      />
                      <Input
                        name="address.uf"
                        placeholder="Estado"
                        value={address.uf ?? ''}
                        control={control}
                        variant="standard"
                        error={formSubmited ? !!errors.address?.uf : false}
                        errorText={formSubmited ? errors.address?.uf?.message : ''}
                        endAdornment={
                          <IconButton disabled>
                            <Lock />
                          </IconButton>
                        }
                        disabled
                      />
                    </div>
                    <div className="footer">
                      <div className="containerStreet">
                        <Input
                          name="address.street"
                          className="street"
                          placeholder="Endereço"
                          value={address?.street ?? ''}
                          control={control}
                          variant="standard"
                          lengthInput={40}
                          error={formSubmited ? !!errors.address?.street : false}
                          errorText={formSubmited ? errors.address?.street?.message : ''}
                          endAdornment={
                            <IconButton disabled>
                              <Lock />
                            </IconButton>
                          }
                          disabled={!isZipCodeWithOutStreet}
                        />
                      </div>
                      <div className="containerNumber">
                        <Input
                          name="address.number"
                          placeholder="Número"
                          value={address.number?.toString() ?? ''}
                          control={control}
                          variant="standard"
                          lengthInput={5}
                          mask={MASK_NUMBER_ADDRESS}
                          preventNotNumbers
                          error={formSubmited ? !!errors.address?.number : false}
                          errorText={formSubmited ? errors.address?.number?.message : ''}
                        />
                      </div>
                      <div className="containerComplement">
                        <Input
                          name="address.complement"
                          className="complement"
                          placeholder="Complemento"
                          value={address.complement ?? ''}
                          control={control}
                          variant="standard"
                          lengthInput={30}
                        />
                      </div>
                    </div>
                  </div>
                </Accordion>
              </div>
            )}
            {data.isServiceProvider && (
              <div className="summaryItens">
                <button
                  onClick={() => {
                    setIsOpenEditDistricts(true);
                  }}
                  type="button"
                  className="titleSummary">
                  Editar suas comarcas de atuação
                </button>
                <div className="contentCities">
                  {districtsSelected.map((district) => (
                    <div key={district.id}>
                      <div className="cities">
                        <button
                          type="button"
                          className="cityName"
                          onClick={() => setIsOpenEditDistricts(true)}>
                          {district.name} - {UF_KEYS[changeSpaceToUnderline(district.uf)]}
                        </button>
                      </div>
                    </div>
                  ))}
                  {formSubmited && data.isServiceProvider && !districtsSelected.length && (
                    <span className="fieldError">Selecione pelo menos uma comarca de atuação</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </Content>
      )}
      {displaySummary && (
        <Content id="expand-register-service">
          <div className="headerContent">
            <h1>
              Você deseja prestar algum tipo serviço?{' '}
              <button type="button" onClick={handleHiddenSummary}>
                {data.isServiceProvider ? 'Sim' : 'Não'}
              </button>{' '}
            </h1>
            <IconButton onClick={handleHiddenSummary}>
              <ExpandCircleDownIcon
                color={
                  Object.keys(address).length > 0 && userType === TypeUser.USER
                    ? 'success'
                    : 'error'
                }
                fontSize="large"
              />
            </IconButton>
          </div>
          {data.isServiceProvider && (
            <div>
              {data.isServiceProvider && userType === TypeUser.USER && (
                <div className="summaryItens">
                  <button onClick={handleHiddenSummary} type="button" className="titleSummary">
                    Seu endereço
                  </button>
                  <div
                    className={
                      !handleGetAddressError(data.address).hasError
                        ? 'contentAddress'
                        : 'displayNone'
                    }>
                    <button type="button" className="address" onClick={handleHiddenSummary}>
                      {data.address.street}, {data.address.number} - {data.address.city} -{' '}
                      {data.address.uf}
                    </button>
                  </div>
                </div>
              )}
              <div className="summaryItens">
                <button onClick={handleHiddenSummary} type="button" className="titleSummary">
                  Comarca de atuação
                </button>
                <div className="contentCities">
                  {districtsSelected.map((district) => (
                    <div key={district.id}>
                      <div className="cities">
                        <button type="button" className="cityName" onClick={handleHiddenSummary}>
                          {district.name} - {UF_KEYS[changeSpaceToUnderline(district.uf)]}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Content>
      )}
      {/* <SendSuggestionModal
        typeService={suggestionIsSpecialty ? 'uma nova especialidade' : 'um novo serviço'}
        isOpen={displaySendSuggestionModal}
        onRequestClose={() => setDisplaySendSuggestionModal(false)}
      /> */}
      <UpdateDistrictsOfOperationModal
        isOpen={isOpenEditDistricts}
        onRequestClose={() => setIsOpenEditDistricts(false)}
      />
    </Container>
  );
});
